import { useState } from 'react';
import { Card } from '@/components/molecules/Card';
import {
  IconButton,
  Checkbox,
  Menu,
  MenuItem,
  Chip,
  Icon,
} from '@/components/atoms';
import { Box, Typography, useTheme } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { DocumentCardProps, DocumentAction } from './DocumentCard.props';

export const DocumentCard = ({
  fileName,
  uploadDate,
  documentType,
  status,
  iconSrc,
  statusColor = 'primary',
  onMoreClick,
  onSelectClick,
  onClick,
  maxWidth = '100%',
  allowSelection = false,
  allowClick = false,
  isSelected = false,
  actions = [],
}: DocumentCardProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onMoreClick?.();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action: DocumentAction) => {
    action.onClick();
    handleMenuClose();
  };

  return (
    <Card
      onClick={allowClick ? onClick : undefined}
      sx={{ maxWidth, gap: 2 }}
      cardIcon={{
        icon: (
          <Icon
            src={iconSrc}
            alt="document icon"
            sx={{ width: 28, height: 28, fontSize: 28 }}
          />
        ),
        position: 'left',
        padding: 0,
      }}
      header={{
        title: (
          <Typography
            sx={{
              ...theme.typography.M3.label.large,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {fileName}
          </Typography>
        ),
        subheader: (
          <Typography
            sx={{
              ...theme.typography.M3.body.small,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {uploadDate}
          </Typography>
        ),
        action: (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'start',
            }}
          >
            <IconButton
              id="document-menu-trigger"
              size="s"
              aria-label="Open document menu"
              aria-controls={isMenuOpen ? 'document-card-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isMenuOpen ? 'true' : undefined}
              onClick={handleMenuOpen}
            >
              <MoreHorizIcon />
            </IconButton>
            <Checkbox
              size="s"
              checked={isSelected}
              onClick={allowSelection ? onSelectClick : undefined}
              slotProps={{ input: { 'aria-label': 'Select document' } }}
            />
            <Menu
              id="document-card-menu"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              slotProps={{
                paper: { 'aria-labelledby': 'document-menu-trigger' },
              }}
            >
              {actions.map((action) => (
                <MenuItem
                  key={action.id}
                  onClick={() => handleActionClick(action)}
                >
                  {action.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        ),
      }}
      content={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'row',
            gap: 1.5,
          }}
        >
          <Typography
            sx={{
              ...theme.typography.M3.body.small,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Document Type: <strong>{documentType}</strong>
          </Typography>
          <Chip
            label={status}
            variant={'outlined'}
            color={statusColor}
            size="s"
            clickable={false}
          />
        </Box>
      }
    />
  );
};

export default DocumentCard;
